import {
  Discount,
  DiscountEligibility,
  DiscountLocationRef,
  DiscountType,
  RenewalCycle,
} from '../models/discount.models';
import { DISCOUNT_TYPE_BADGE_LABELS, ELIGIBILITY_LABELS } from '../constants/discount.constants';

/** Number(null) / Number('') are 0, so blanks must be rejected before coercion. */
function toFiniteNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

/**
 * Discount amount as shown in both tables: `20%` for percentage, `$150` for flat.
 * Mirrors the server-side formatter used for the Custom Discount CSV.
 */
export function formatDiscountAmount(
  discountType: DiscountType | string | null | undefined,
  discountValue: string | number | null | undefined
): string {
  const amount = toFiniteNumber(discountValue);
  if (amount === null) {
    return '-';
  }
  const trimmed = String(Number(amount.toFixed(2)));
  return discountType === 'percentage' ? `${trimmed}%` : `$${trimmed}`;
}

/**
 * The Type badge merges discount_type and renewal_cycle: an advance renewal cycle
 * is more specific than flat/percentage, so it wins (see the Discounts Summary design).
 */
export function resolveDiscountTypeBadge(discount: Discount): {
  key: string;
  label: string;
} {
  const cycle: RenewalCycle = discount.renewal_cycle || 'standard';
  const key = cycle === 'standard' ? discount.discount_type : cycle;
  return { key, label: DISCOUNT_TYPE_BADGE_LABELS[key] || key };
}

/** Eligibility cell / details / CSV label from ELIGIBILITY_OPTIONS (via ELIGIBILITY_LABELS). */
export function formatEligibilityLabel(
  eligibility: DiscountEligibility | string | null | undefined
): string {
  const key = (eligibility || 'all_customer') as DiscountEligibility;
  return ELIGIBILITY_LABELS[key] || String(eligibility || 'all_customer');
}

/** CSS modifier for the Type tag, e.g. `advance-custom` (matches Customers Customer Type tag). */
export function discountTypeBadgeClass(discount: Discount): string {
  return resolveDiscountTypeBadge(discount).key.replace(/_/g, '-');
}

export function discountStatusLabel(discount: Discount): string {
  return discount.is_active === false ? 'Inactive' : 'Active';
}

export function discountStatusClass(discount: Discount): string {
  return discount.is_active === false ? 'status--inactive' : 'status--active';
}

/** Customer Type tag modifier for listing tables (6px radius, not status-pill). */
export function discountListingStatusClass(discount: Discount): string {
  return discount.is_active === false ? 'inactive' : 'active';
}

/**
 * Location cell text. No locations means the discount applies everywhere, which
 * the API represents as an empty list.
 */
export function formatDiscountLocations(
  locations: DiscountLocationRef[] | undefined | null
): string {
  if (!locations || locations.length === 0) {
    return 'All Locations';
  }
  return locations.map(location => location.name).join(', ');
}

/** Times Applied cell; the API counts orders, so 0 is a real value. */
export function formatTimesApplied(discount: Discount): string {
  const value = toFiniteNumber(discount.times_applied);
  return value === null ? '0' : String(value);
}

/** `14:30` (DB time) → `2:30 PM`. Returns '' for blank input. */
export function formatTimeOfDay(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const [hourPart, minutePart] = String(value).split(':');
  const hour24 = Number(hourPart);
  if (!Number.isFinite(hour24)) {
    return '';
  }
  const period = hour24 < 12 ? 'AM' : 'PM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${(minutePart || '00').slice(0, 2)} ${period}`;
}

/** Trims `14:30:00` (Postgres TIME) down to the `HH:mm` the dropdowns use. */
export function normalizeTimeForForm(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const parts = String(value).split(':');
  if (parts.length < 2) {
    return '';
  }
  return `${parts[0].padStart(2, '0')}:${parts[1].slice(0, 2)}`;
}

/** ISO timestamp → `YYYY-MM-DD` for date inputs. */
export function toDateInputValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}
